import { Box, Typography } from '@mui/material';
import { fmtBaths, fmtSize } from './format';
import { border, text } from '../../theme/colors';

/**
 * Utilities are not their own columns in the database — the realtor ticks them
 * off the feature list. These groupings turn those ticks back into the rows a
 * buyer looks for ("Water", "Power", "Security") without inventing anything:
 * the row appears only when the realtor actually ticked something in it.
 */
const GROUPS = [
  { label: 'Water', of: ['Borehole', 'Council water', 'Sewer connected', 'Septic tank'] },
  { label: 'Power', of: ['Solar backup', 'Generator'] },
  { label: 'Security', of: ['Electric fence', 'Durawall', 'Alarm', 'Gate motor'] },
];

/** "Solar backup" reads as a heading; inside a sentence of utilities it reads as "solar backup". */
const lower = (s, i) => (i === 0 ? s : s.charAt(0).toLowerCase() + s.slice(1));

/**
 * "Property details": the full record behind the key-facts strip, as a plain
 * two-column table. Every row is omitted when its value is missing, so a sparse
 * listing shows a short honest table rather than a long one full of dashes.
 */
export default function DetailsTable({ listing }) {
  const l = listing || {};
  const isHouse = l.listing_type === 'house';
  const features = Array.isArray(l.features) ? l.features : [];

  const rows = [
    // Tenure: a title-deed listing is freehold (the deeds search establishes it);
    // a cession is rights from a council or developer, not yet a title.
    { label: 'Type', value: `${isHouse ? 'House' : 'Stand'} · ${l.tenure_type === 'cession' ? 'cession (no title deed yet)' : 'freehold'}` },
    { label: 'Year built', value: isHouse && l.year_built != null ? String(l.year_built) : null },
    { label: 'Bedrooms', value: isHouse && l.bedrooms != null ? String(l.bedrooms) : null },
    { label: 'Bathrooms', value: isHouse && l.bathrooms != null ? fmtBaths(l.bathrooms) : null },
    { label: 'Floor area', value: isHouse ? fmtSize(l.floor_area_sqm) : null },
    { label: 'Stand size', value: fmtSize(l.stand_size_sqm) },
    { label: 'Parking', value: l.parking || null },
    ...GROUPS.map((g) => {
      const hits = g.of.filter((f) => features.includes(f));
      return { label: g.label, value: hits.length ? hits.map(lower).join(' · ') : null };
    }),
  ].filter((r) => r.value);

  if (rows.length === 0) return null;

  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        columnGap: 4,
        rowGap: 0,
        m: 0,
      }}
    >
      {rows.map((r) => (
        <Box
          key={r.label}
          sx={{
            display: 'flex', justifyContent: 'space-between', gap: 2,
            py: 1.25, borderBottom: `1px solid ${border.light}`,
          }}
        >
          <Typography component="dt" variant="body2" sx={{ color: text.secondary }}>{r.label}</Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, color: text.primary, fontWeight: 600, textAlign: 'right' }}>
            {r.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
